import PropTypes from 'prop-types';
import { CHIP_GREEN, CHIP_AMBER, CHIP_GREY } from 'src/components/actionCards/actionCardColors';
import { Box, Typography, Avatar, alpha, Tooltip } from '@mui/material';
import { Iconify } from 'src/components';
import DemoBlur from 'src/components/DemoBlur';

const TOOLTIP_SX = { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 };

const RANK_STYLES = {
    1: {
        border: '2px solid #fcd34d',
        gradient: 'linear-gradient(90deg, #fbbf24, #f59e0b)',
        badgeBg: '#fef3c7',
        badgeColor: '#d97706',
        label: '1st',
    },
    2: {
        border: '2px solid #cbd5e1',
        gradient: 'linear-gradient(90deg, #cbd5e1, #94a3b8)',
        badgeBg: '#f1f5f9',
        badgeColor: '#64748b',
        label: '2nd',
    },
    3: {
        border: '2px solid #fdba74',
        gradient: 'linear-gradient(90deg, #fdba74, #f97316)',
        badgeBg: '#fed7aa',
        badgeColor: '#c2410c',
        label: '3rd',
    },
};

export default function LeaderboardCards({ practitioners }) {
    const top3 = practitioners.slice(0, 3);

    const getInitials = (name) => {
        if (!name) return '?';
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const formatCurrency = (value) => {
        if (value >= 1000000) return `£${(value / 1000000).toFixed(1)}M`;
        if (value >= 1000) return `£${(value / 1000).toFixed(1)}K`;
        return `£${value}`;
    };

    const getConvRateColor = (rate) => {
        const numRate = parseFloat(rate) || 0;
        // Green / amber / GREY — never red (red is reserved for overdue/late).
        if (numRate >= 50) return CHIP_GREEN.text;
        if (numRate >= 30) return CHIP_AMBER.text;
        return CHIP_GREY.text;
    };

    return (
        <Box
            sx={{
                display: 'flex',
                gap: '16px',
                mb: 3,
                flexWrap: 'wrap',
            }}
        >
            {top3.map((practitioner, index) => {
                const rank = index + 1;
                const style = RANK_STYLES[rank];
                const convRate = parseFloat(practitioner.conversionRate) || 0;
                const convRateColor = getConvRateColor(convRate);

                return (
                    <Box
                        key={practitioner.id}
                        sx={{
                            flex: '1 1 300px',
                            bgcolor: '#fff',
                            borderRadius: '16px',
                            p: '24px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                            textAlign: 'center',
                            position: 'relative',
                            overflow: 'hidden',
                            border: style.border,
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '4px',
                                background: style.gradient,
                            },
                        }}
                    >
                        {/* Rank badge */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '16px',
                                right: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                bgcolor: style.badgeBg,
                                color: style.badgeColor,
                                px: 1.5,
                                py: 0.5,
                                borderRadius: '20px',
                                fontWeight: 700,
                                fontSize: '13px',
                            }}
                        >
                            {rank === 1 && <Iconify icon="eva:award-fill" width={16} height={16} />}
                            {style.label}
                        </Box>

                        {/* Avatar */}
                        <DemoBlur component="div" sx={{ display: 'block' }}>
                        <Avatar
                            src={practitioner.avatar}
                            sx={{
                                width: '80px',
                                height: '80px',
                                margin: '0 auto 12px',
                                bgcolor: '#e2e8f0',
                                color: '#64748b',
                                fontWeight: 700,
                                fontSize: '24px',
                            }}
                        >
                            {getInitials(practitioner.name)}
                        </Avatar>
                        </DemoBlur>

                        {/* Name */}
                        <Typography sx={{ fontSize: '18px', fontWeight: 600, mb: 0.25 }}>
                            <DemoBlur>{practitioner.name}</DemoBlur>
                        </Typography>

                        {/* Specialty */}
                        {practitioner.specialty && (
                            <Typography sx={{ fontSize: '12px', color: '#94a3b8', mb: 1.5 }}>
                                {practitioner.specialty}
                            </Typography>
                        )}

                        {/* Conversion Rate - hero stat */}
                        <Tooltip title="Percentage of attended consultations that led to treatment start." placement="top" arrow
                            slotProps={{ tooltip: { sx: TOOLTIP_SX } }}>
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'baseline',
                                    gap: 0.5,
                                    bgcolor: alpha(convRateColor, 0.1),
                                    px: 2,
                                    py: 0.75,
                                    borderRadius: '10px',
                                    mb: 2,
                                    cursor: 'help',
                                }}
                            >
                                <Typography sx={{ fontSize: '28px', fontWeight: 700, color: convRateColor }}>
                                    {convRate}%
                                </Typography>
                                <Typography sx={{ fontSize: '11px', color: convRateColor, fontWeight: 600 }}>
                                    conversion
                                </Typography>
                            </Box>
                        </Tooltip>

                        {/* Stats row */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: 2.5,
                                pt: 2,
                                borderTop: '1px solid #e2e8f0',
                            }}
                        >
                            <Tooltip title="Total attended consultations." placement="bottom" arrow
                                slotProps={{ tooltip: { sx: TOOLTIP_SX } }}>
                                <Box sx={{ textAlign: 'center', cursor: 'help' }}>
                                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#8b5cf6' }}>
                                        {practitioner.consultations || 0}
                                    </Typography>
                                    <Typography sx={{ fontSize: '11px', color: '#64748b' }}>
                                        Consultations
                                    </Typography>
                                </Box>
                            </Tooltip>

                            <Tooltip title="Total treatments started from consultations." placement="bottom" arrow
                                slotProps={{ tooltip: { sx: TOOLTIP_SX } }}>
                                <Box sx={{ textAlign: 'center', cursor: 'help' }}>
                                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#1e293b' }}>
                                        {practitioner.conversions || 0}
                                    </Typography>
                                    <Typography sx={{ fontSize: '11px', color: '#64748b' }}>
                                        Conversions
                                    </Typography>
                                </Box>
                            </Tooltip>

                            <Tooltip title="Average revenue per treatment conversion." placement="bottom" arrow
                                slotProps={{ tooltip: { sx: TOOLTIP_SX } }}>
                                <Box sx={{ textAlign: 'center', cursor: 'help' }}>
                                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#1e293b' }}>
                                        {formatCurrency(practitioner.avgRevenue || 0)}
                                    </Typography>
                                    <Typography sx={{ fontSize: '11px', color: '#64748b' }}>
                                        Avg/Case
                                    </Typography>
                                </Box>
                            </Tooltip>
                        </Box>
                    </Box>
                );
            })}

            {top3.length === 0 && (
                <Box
                    sx={{
                        width: '100%',
                        textAlign: 'center',
                        py: 6,
                        bgcolor: '#fff',
                        borderRadius: '16px',
                    }}
                >
                    <Typography sx={{ color: '#64748b' }}>
                        No team member data available
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

LeaderboardCards.propTypes = {
    practitioners: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            name: PropTypes.string.isRequired,
            avatar: PropTypes.string,
            specialty: PropTypes.string,
            consultations: PropTypes.number,
            conversions: PropTypes.number,
            revenue: PropTypes.number,
            conversionRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            avgRevenue: PropTypes.number,
        })
    ).isRequired,
};
